import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Quotation, QuotationCreate, QuotationDetail, QuotationUpdate } from '@core/types/quotation.type';
import { QuotationService } from '@core/services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class QuotationQuery {
  private readonly service = inject(QuotationService);
  private readonly queryClient = inject(QueryClient);

  useQuotationsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.quotations.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<Quotation>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useQuotationDetail(id: () => string | null) {
    return injectQuery(() => {
      const v = id();
      return {
        queryKey: QUERY_KEYS.quotations.detail(v || ''),
        queryFn: () => lastValueFrom(this.service.get(v!)),
        select: (res: ApiResponse<QuotationDetail>) => res.data,
        enabled: !!v,
      };
    });
  }

  useCreateQuotation() {
    return injectMutation(() => ({
      mutationFn: (data: QuotationCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quotations.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
      },
    }));
  }

  useUpdateQuotation() {
    return injectMutation(() => ({
      mutationFn: (data: QuotationUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quotations.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quotations.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
      },
    }));
  }

  useDeleteQuotation() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quotations.all }),
    }));
  }
}

